// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
import React, { useState, useEffect } from 'react';
import { transitions } from '../utils/animations';

/**
 * Toast Notification System
 * Displays temporary notifications to users
 */

let toastQueue = [];
let addToastCallback = null;

export const showToast = (message, type = 'info', duration = 3000) => {
  const toast = {
    id: Date.now() + Math.random(),
    message,
    type,
    duration
  };
  
  if (addToastCallback) {
    addToastCallback(toast);
  } else {
    toastQueue.push(toast);
  }
};

const Toast = () => {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    // Set callback for adding toasts
    addToastCallback = (toast) => {
      setToasts(prev => [...prev, toast]);
      
      // Auto remove after duration
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== toast.id));
      }, toast.duration);
    };

    // Process queued toasts
    if (toastQueue.length > 0) {
      toastQueue.forEach(toast => addToastCallback(toast));
      toastQueue = [];
    }

    return () => {
      addToastCallback = null;
    };
  }, []);

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const getToastStyle = (type) => {
    switch (type) {
      case 'success':
        return 'bg-green-600 text-white';
      case 'error':
        return 'bg-red-600 text-white';
      case 'warning':
        return 'bg-orange-600 text-white';
      case 'info':
      default:
        return 'bg-blue-600 text-white';
    }
  };

  const getToastIcon = (type) => {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      case 'info':
      default:
        return 'ℹ';
    }
  };

  return (
    <div className="fixed top-4 right-4 z-[9999] space-y-2 pointer-events-none">
      {toasts.map(toast => (
        <div
          key={toast.id}
          className={`
            ${getToastStyle(toast.type)}
            px-6 py-4 rounded-lg shadow-2xl
            max-w-md pointer-events-auto
            animate-slideInRight
            ${transitions.default}
          `}
          role="alert"
          aria-live="polite"
        >
          <div className="flex items-start gap-3">
            <span className="text-2xl flex-shrink-0" aria-hidden="true">
              {getToastIcon(toast.type)}
            </span>
            <div className="flex-1">
              <p className="font-medium">{toast.message}</p>
            </div>
            <button
              onClick={() => removeToast(toast.id)}
              className="flex-shrink-0 text-white/80 hover:text-white transition"
              aria-label="Close notification"
            >
              ✕
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default Toast;
